import type {
  CollectionItemAllowOperationsEvent,
  DragDropAllowEvent,
  ElementAllowOperationsEvent,
  ElementDeletingEvent,
  ElementGetActionsEvent,
  ModifiedEvent,
  PageAddedEvent,
  PageGetFooterActionsEvent,
  PropertyGridSurveyCreatedEvent,
  PropertyShowingEvent,
  PropertyValueChangingEvent,
  QuestionAddedEvent,
  SurveyCreatorModel,
  SurveyInstanceCreatedEvent,
} from 'survey-creator-core';
import {
  Action,
  SvgRegistry,
  type Base,
  type PageModel,
  type Question,
  type SurveyModel,
} from 'survey-core';
import {
  HIDE_ACTION_ID,
  REQUIRED_ACTION_ID,
  additionalPageName,
  allowOperationsFor,
  classify,
  guardPropertyChange,
  inlineActionIdsFor,
  insertionIndexForNewPage,
  isPageOrderAllowed,
  isPanelVisible,
  isPropertyVisible,
  isLogicProperty,
  isQuestionDropAllowed,
  levelFor,
  logicRuleRefusalMessage,
  type AlignmentClassifier,
  type AlignmentRestrictions,
  type PropertyGridLevel,
} from './alignment-restrictions';

/**
 * DEV-269 — liga as decisões puras de `alignment-restrictions.ts` aos eventos
 * do SurveyJS Creator 2.5 (spec 138, research.md R3). Tudo POR INSTÂNCIA: nada
 * de `Serializer.addProperty` nem de mexer em
 * `SurveyQuestionEditorDefinition` (globais — vazariam para o construtor de
 * candidato, SC-16).
 *
 * `onRejected(message)` é como a tela explica uma recusa (Sonner). A última
 * barreira continua sendo o Core (422).
 */
export interface AlignmentBinding {
  restrictions: AlignmentRestrictions;
  onRejected: (message: string) => void;
  /**
   * Página adicional com perguntas pede confirmação antes de apagar (AC-44).
   * O binder recusa a exclusão nativa e delega ao container, que confirma e
   * chama `deletePage(page)`.
   */
  onConfirmDeletePage: (page: PageModel, deletePage: () => void) => void;
}

type ObjLike = Base & { name?: string };

function objectLike(obj: Base | undefined | null): { type: string; name?: string } {
  if (!obj) return { type: 'unknown' };
  return { type: obj.getType(), name: (obj as ObjLike).name };
}

function levelOf(cls: AlignmentClassifier, obj: Base | undefined | null): PropertyGridLevel | null {
  if (!obj) return null;
  return levelFor(cls, objectLike(obj));
}

/** Página que contém um elemento (pergunta ou painel), subindo pelos pais. */
function pageNameOf(element: unknown): string | null {
  let current = element as {
    page?: { name?: string };
    parent?: unknown;
    getType?: () => string;
    name?: string;
  } | null;
  let guard = 0;
  while (current && guard < 32) {
    if (typeof current.getType === 'function' && current.getType() === 'page') {
      return current.name ?? null;
    }
    if (current.page && typeof current.page.name === 'string') return current.page.name;
    current = (current.parent as typeof current) ?? null;
    guard += 1;
  }
  return null;
}

const CONFIRMED_DELETE = new WeakSet<object>();

/**
 * Ícone da ação "Ocultar" da pergunta padrão: olho cortado, como no Figma
 * ("Edit alignment form / Edit default question", node 16092:35835 — QA 09/10).
 * O desenho é o `eye-off` da Lucide, a mesma família dos ícones do app. O
 * Creator só desenha ícones registrados no `SvgRegistry`.
 */
export const HIDE_ICON_ID = 'icon-alignment-hide';
const HIDE_ICON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>' +
  '<path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/>' +
  '<path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/>' +
  '<line x1="2" x2="22" y1="2" y2="22"/>' +
  '</svg>';
let hideIconRegistered = false;
function registerHideIcon(): void {
  if (hideIconRegistered) return;
  hideIconRegistered = true;
  SvgRegistry.registerIconFromSvg(HIDE_ICON_ID, HIDE_ICON_SVG);
}

/**
 * O survey-core monta as linhas do canvas sob demanda (lazy render) e SUSPENDE essa
 * montagem enquanto rola até um elemento selecionado; com rolagem programática a suspensão
 * fica pendurada e as perguntas abaixo da dobra nunca montam (visto no e2e: 5 de 13 perguntas
 * da 1ª página). O formulário de alinhamento tem poucas dezenas de perguntas: montar tudo de
 * uma vez não custa nada e o canvas fica íntegro. Só nesta instância (SC-16).
 */
function disableLazyRendering(survey: SurveyModel): void {
  (survey as SurveyModel & { lazyRendering?: boolean }).lazyRendering = false;
}

/**
 * Relê a lista da aba "Lógica" depois que uma regra foi desfeita: sem isso a aba
 * continua exibindo "Se 'pergunta1' == 'x', torna questão … visível" até a pessoa
 * trocar de aba. Adiado: a reversão acontece DENTRO do "Terminar" da própria aba.
 */
function refreshLogicTab(creator: SurveyCreatorModel): void {
  setTimeout(() => {
    if (creator.activeTab !== 'logic') return;
    const plugin = creator.getPlugin('logic') as { update?: () => void } | undefined;
    plugin?.update?.();
  }, 0);
}

const SPECIAL_ITEMS_HIDDEN = new WeakSet<object>();

/**
 * No designer o survey-core mostra "Nenhum", "Outros (descrever)", "Selecionar
 * tudo" e o "+ item" como opções-fantasma em TODA pergunta de escolha
 * (`settings.showDefaultItemsInCreator`), para quem quiser ligá-las. Na
 * pergunta padrão a lista de opções é intocável (AC-38), então só as opções
 * reais (`question.choices`) ficam visíveis — pelo evento do survey do designer
 * (`onShowingChoiceItem`), por instância, nunca pelo setting global, que
 * vazaria para o construtor de candidato (SC-16). O callback por pergunta
 * (`setCanShowOptionItemCallback`) não serve: o adorner do Creator o
 * sobrescreve a cada render. A pergunta adicional continua com as fantasmas.
 */
function hideDesignerSpecialItems(cls: AlignmentClassifier, survey: SurveyModel): void {
  if (!SPECIAL_ITEMS_HIDDEN.has(survey)) {
    SPECIAL_ITEMS_HIDDEN.add(survey);
    survey.onShowingChoiceItem.add((_s, options) => {
      if (!cls.isDefaultQuestion(options.question.name)) return;
      const real = (options.question as Question & { choices?: unknown[] }).choices ?? [];
      if (!real.includes(options.item)) options.visible = false;
    });
  }
  // As fantasmas já estão na lista visível em cache; só a recomposição as tira.
  for (const question of survey.getAllQuestions(false, true) as (Question & {
    onVisibleChoicesChanged?: () => void;
  })[]) {
    if (cls.isDefaultQuestion(question.name)) question.onVisibleChoicesChanged?.();
  }
}

export function bindAlignmentRestrictions(
  creator: SurveyCreatorModel,
  binding: AlignmentBinding
): () => void {
  const cls = classify(binding.restrictions);
  const disposers: (() => void)[] = [];
  const on = <T>(
    event: {
      add: (fn: (sender: SurveyCreatorModel, options: T) => void) => void;
      remove: (fn: (sender: SurveyCreatorModel, options: T) => void) => void;
    },
    handler: (sender: SurveyCreatorModel, options: T) => void
  ) => {
    event.add(handler);
    disposers.push(() => event.remove(handler));
  };

  // ---- Barra de categorias (AC-29b / AC-32b / AC-46b) ----------------------
  on<PropertyGridSurveyCreatedEvent>(creator.onPropertyGridSurveyCreated, (_s, options) => {
    const level = levelOf(cls, options.obj);
    if (!level) return;
    const survey = options.survey;
    // No modo `buttons` (padrão do Creator — `showOneCategoryInPropertyGrid`)
    // cada categoria é uma PÁGINA do survey do property grid, nomeada pela
    // chave da categoria (general, pages, logic, layout, navigation, …):
    // `PropertyJSONGenerator.convertPanelsToPages`. É essa lista de páginas
    // que vira a barra de ícones da direita. Remover a página tira o ícone e
    // tira o conteúdo da busca "Escreva para pesquisar..." (borda do card).
    for (const page of [...survey.pages]) {
      if (isPanelVisible(level, page.name)) continue;
      page.visible = false;
      survey.removePage(page);
    }
    // Painel "Páginas" (nível formulário): a matriz de páginas tem alça de
    // arrastar por linha — reordenar por aqui moveria página padrão (AC-33b).
    // Reordenação de página adicional segue possível no canvas (AC-33c).
    if (level === 'survey') {
      const pagesMatrix = survey.getQuestionByName('pages') as unknown as {
        allowRowReorder?: boolean;
        allowRowsDragAndDrop?: boolean;
      } | null;
      if (pagesMatrix) {
        pagesMatrix.allowRowReorder = false;
        pagesMatrix.allowRowsDragAndDrop = false;
      }
    }
    // No modo `accordion` as categorias são painéis — mesma regra por nome.
    const panels = survey.getAllPanels(false, true) as unknown as {
      name: string;
      visible: boolean;
      delete?: () => void;
    }[];
    for (const panel of panels) {
      if (isPanelVisible(level, panel.name)) continue;
      panel.visible = false;
      panel.delete?.();
    }
  });

  // ---- Controles do painel (AC-32 / AC-37) -----------------------------------
  on<PropertyShowingEvent>(creator.onPropertyShowing, (_s, options) => {
    const level = levelOf(cls, options.obj);
    if (!level) return;
    if (!isPropertyVisible(level, options.property.name)) options.show = false;
  });

  // ---- Adorner: operações permitidas (AC-33 / AC-36 / AC-38) ----------------
  on<ElementAllowOperationsEvent>(creator.onElementAllowOperations, (_s, options) => {
    const level = levelOf(cls, options.element ?? options.obj);
    if (!level) return;
    Object.assign(options, allowOperationsFor(level));
  });

  // ---- Adorner: ações inline exatas da pergunta padrão (AC-36) --------------
  registerHideIcon();
  on<ElementGetActionsEvent>(creator.onElementGetActions, (_s, options) => {
    const level = levelOf(cls, options.element);
    if (level !== 'default-question') return;
    const question = options.element as Question;
    const hide = new Action({
      id: HIDE_ACTION_ID,
      title: 'Ocultar',
      iconName: HIDE_ICON_ID,
      iconSize: 'auto',
      visibleIndex: 20,
      action: () => {
        const guard = guardPropertyChange(cls, {
          kind: 'question',
          name: question.name,
          property: 'visible',
          newValue: false,
        });
        if (!guard.allow) {
          binding.onRejected(guard.message ?? '');
          return;
        }
        question.visible = false;
      },
    });
    const required = new Action({
      id: REQUIRED_ACTION_ID,
      title: 'É obrigatório?',
      iconName: 'icon-required-16x16',
      iconSize: 'auto',
      css: 'svc-action-bar-item--right sv-action--isrequired',
      innerCss: 'svc-required-action',
      ariaRole: 'checkbox',
      ariaChecked: question.isRequired,
      active: question.isRequired,
      visibleIndex: 10,
      action: () => {
        const next = !question.isRequired;
        const guard = guardPropertyChange(cls, {
          kind: 'question',
          name: question.name,
          property: 'isRequired',
          newValue: next,
        });
        if (!guard.allow) {
          binding.onRejected(guard.message ?? '');
          return;
        }
        question.isRequired = next;
      },
    });
    // Mudança vinda do painel ("É obrigatório?" do property grid) reflete no adorner.
    question.registerFunctionOnPropertyValueChanged(
      'isRequired',
      () => {
        required.active = question.isRequired;
        required.ariaChecked = question.isRequired;
      },
      'alignmentRequiredAdorner'
    );
    const allowed = inlineActionIdsFor(level);
    const kept = allowed
      ? options.actions.filter((a) => allowed.includes(a.id ?? ''))
      : options.actions;
    options.actions.splice(0, options.actions.length, ...kept, required, hide);
  });

  // ---- Rodapé da página padrão: sem "Adicionar Questão" (AC-47b) ------------
  on<PageGetFooterActionsEvent>(creator.onPageGetFooterActions, (_s, options) => {
    if (cls.isDefaultPage(options.page.name)) options.actions.splice(0, options.actions.length);
  });

  // ---- Coleções travadas (AC-33 / AC-38) --------------------------------------
  on<CollectionItemAllowOperationsEvent>(creator.onCollectionItemAllowOperations, (_s, options) => {
    // Painel "Páginas" do formulário: lista as páginas com lixeira e edição
    // inline — página padrão não se apaga nem se renomeia por aqui (AC-33).
    if (options.propertyName === 'pages') {
      const item = options.item as ObjLike;
      if (cls.isDefaultPage(item?.name)) {
        options.allowDelete = false;
        options.allowEdit = false;
      }
      return;
    }
    // Opções (choices/rows/columns…) de pergunta padrão: intocáveis (AC-38).
    if (levelOf(cls, options.element) !== 'default-question') return;
    options.allowAdd = false;
    options.allowDelete = false;
    options.allowEdit = false;
  });

  // ---- Arrastar e soltar (AC-33b / AC-33c / AC-38b / AC-47 / AC-47b) ---------
  on<DragDropAllowEvent>(creator.onDragDropAllow, (_s, options) => {
    const dragged = options.draggedElement as unknown as
      | { getType?: () => string; name?: string }
      | undefined;
    const draggedType = dragged?.getType?.();

    if (draggedType === 'page') {
      const pages = creator.survey.pages.map((p) => p.name);
      const draggedName = dragged?.name ?? '';
      if (cls.isDefaultPage(draggedName)) {
        options.allow = false;
        return;
      }
      const target = (options.insertBefore ??
        options.insertAfter ??
        options.toElement) as unknown as { name?: string } | undefined;
      const targetName = target?.name ?? '';
      const without = pages.filter((n) => n !== draggedName);
      const targetIdx = without.indexOf(targetName);
      if (targetIdx < 0) {
        options.allow = false;
        return;
      }
      const insertAt = options.insertAfter && !options.insertBefore ? targetIdx + 1 : targetIdx;
      const resulting = [...without.slice(0, insertAt), draggedName, ...without.slice(insertAt)];
      options.allow = isPageOrderAllowed(cls, resulting);
      return;
    }

    // Pergunta/painel: da paleta (sem nome conhecido) ou do canvas.
    const draggedName = dragged?.name && cls.isDefaultQuestion(dragged.name) ? dragged.name : null;
    const isFromToolbox = !options.fromElement;
    const targetPage =
      pageNameOf(options.parent) ??
      pageNameOf(options.toElement) ??
      pageNameOf(options.insertBefore) ??
      pageNameOf(options.insertAfter);
    options.allow = isQuestionDropAllowed(cls, {
      draggedName: isFromToolbox ? null : (draggedName ?? dragged?.name ?? null),
      targetPage,
    });
  });

  // ---- Pergunta padrão sem as opções-fantasma do designer (AC-38) ----------
  on<SurveyInstanceCreatedEvent>(creator.onSurveyInstanceCreated, (_s, options) => {
    if (options.area !== 'designer-tab') return;
    hideDesignerSpecialItems(cls, options.survey);
    disableLazyRendering(options.survey);
  });
  hideDesignerSpecialItems(cls, creator.survey); // survey já criado antes do bind
  disableLazyRendering(creator.survey);

  // ---- Página nova entra antes de "Descritivos", com nome `páginaN`, e fica
  // selecionada (AC-42) -------------------------------------------------------
  on<PageAddedEvent>(creator.onPageAdded, (_s, options) => {
    if (cls.isDefaultPage(options.page.name)) return; // carga do JSON, nunca reposicionar padrão
    // Adiado para depois da chamada que criou a página: o (+) do painel "Páginas" ainda está
    // inserindo a linha na matriz do property grid quando o evento dispara, e remover/readicionar
    // a página nesse instante derruba a matriz ("Cannot read properties of null (reading 'push')").
    queueMicrotask(() => {
      const page = options.page;
      if (page.isDisposed) return;
      const survey = creator.survey;
      const names = survey.pages.map((p) => p.name);
      const currentIdx = names.indexOf(page.name);
      if (currentIdx < 0) return; // já saiu (apagada no meio-tempo)
      const wanted = insertionIndexForNewPage(
        cls,
        names.filter((n) => n !== page.name)
      );
      if (currentIdx !== wanted) {
        survey.removePage(page);
        survey.addPage(page, wanted);
      }
      // O Creator numera pela contagem de nomes `página*` ("página1" numa 5ª página) enquanto
      // o canvas rotula pela posição ("Página 5"). Só o nome gerado pelo Creator é trocado; um
      // nome dado pela pessoa fica.
      if (/^página\d+$/.test(page.name)) {
        const taken = survey.pages.map((p) => p.name).filter((n) => n !== page.name);
        const coherent = additionalPageName(taken, wanted);
        if (page.name !== coherent) page.name = coherent;
      }
      creator.selectElement(page);
    });
  });

  // ---- Rede de segurança: pergunta caiu numa página padrão (AC-47b) ----------
  on<QuestionAddedEvent>(creator.onQuestionAdded, (_s, options) => {
    if (!cls.isDefaultPage(options.page?.name)) return;
    if (cls.isDefaultQuestion(options.question?.name)) return; // carga do JSON
    options.page.removeElement(options.question);
    binding.onRejected('Página padrão não recebe pergunta nova. Crie uma página adicional.');
  });

  // ---- Apagar (AC-33 / AC-38 / AC-44) ---------------------------------------
  on<ElementDeletingEvent>(creator.onElementDeleting, (_s, options) => {
    const level = levelOf(cls, options.element);
    if (level === 'default-page' || level === 'default-question') {
      options.allow = false;
      return;
    }
    if (level === 'additional-page') {
      const page = options.element as PageModel;
      if (CONFIRMED_DELETE.has(page)) return; // exclusão já confirmada pelo container
      if (page.elements.length === 0) return; // página vazia apaga direto
      options.allow = false;
      binding.onConfirmDeletePage(page, () => {
        CONFIRMED_DELETE.add(page);
        creator.deleteElement(page);
      });
    }
  });

  // ---- Protegidos e elementos padrão travados (AC-41 / AC-41b / AC-33 / AC-38)
  on<PropertyValueChangingEvent>(creator.onPropertyValueChanging, (_s, options) => {
    const obj = options.obj;
    if (!obj) return;
    const type = obj.getType();
    const kind: 'page' | 'question' | null =
      type === 'page' ? 'page' : type === 'survey' ? null : 'question';
    if (!kind) return;
    const guard = guardPropertyChange(cls, {
      kind,
      name: (obj as ObjLike).name,
      property: options.propertyName,
      newValue: options.newValue,
    });
    if (guard.allow) return;
    options.newValue = options.value; // controle não fica alternado na tela
    if (guard.message) binding.onRejected(guard.message);
  });

  // ---- Mudanças fora do painel (aba "Lógica" etc.): revertidas (AC-41 / SC-14) ---
  // A aba "Lógica" grava `visibleIf`/`enableIf`/`requiredIf` direto no elemento, sem
  // passar por onPropertyValueChanging — QA 09/10: uma regra escondia "Código interno
  // da vaga" no "Testar questionário" e só o servidor recusava (422). Tudo o que a
  // guarda recusa num elemento padrão é desfeito aqui; a regra de Lógica ganha uma
  // explicação que nomeia o campo, e a lista da aba "Lógica" é relida (a regra some).
  let reverting = false;
  on<ModifiedEvent>(creator.onModified, (_s, options) => {
    if (reverting || options.type !== 'PROPERTY_CHANGED') return;
    const target = options.target as (ObjLike & { title?: string }) | undefined;
    const property = options.name;
    if (!target || typeof target.getType !== 'function' || typeof property !== 'string') return;
    const type = target.getType();
    const kind: 'page' | 'question' | null =
      type === 'page' ? 'page' : type === 'survey' ? null : 'question';
    if (!kind) return;
    const guard = guardPropertyChange(cls, {
      kind,
      name: target.name,
      property,
      newValue: options.newValue,
    });
    if (guard.allow) return;
    reverting = true;
    try {
      target.setPropertyValue(property, options.oldValue);
    } finally {
      reverting = false;
    }
    if (isLogicProperty(property)) {
      const label = target.title || target.name || '';
      const isProtected =
        kind === 'page' ? cls.isProtectedPage(target.name) : cls.isProtectedQuestion(target.name);
      binding.onRejected(logicRuleRefusalMessage(kind, label, isProtected));
      refreshLogicTab(creator);
      return;
    }
    if (guard.message) binding.onRejected(guard.message);
  });

  return () => {
    for (const dispose of disposers) dispose();
  };
}
